import type { LucideIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";

interface EmptyStateProps {
  /** Icono Lucide a mostrar dentro del círculo. */
  icon?: LucideIcon;
  /** Título principal — debe ser corto y declarativo. */
  title: string;
  /** Descripción opcional — explica por qué está vacío y/o invita a actuar. */
  description?: string;
  /**
   * CTA(s) al pie. Suele ser 1 botón primario + 1 secundario.
   * Si la pantalla no permite crear, dejarlo undefined.
   */
  children?: React.ReactNode;
  /** Tono visual del icon. */
  tone?: "brand" | "slate" | "emerald" | "amber" | "rose";
  /** Alto del bloque. compact = py-10, default = py-16, hero = py-24. */
  size?: "compact" | "default" | "hero";
  /** Clases extra. */
  className?: string;
}

const TONE_CLS: Record<NonNullable<EmptyStateProps["tone"]>, string> = {
  brand: "bg-brand-50 text-brand-600",
  // `slate` se conserva como nombre de tono (API pública); pinta neutral.
  slate: "bg-neutral-100 text-neutral-500",
  emerald: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
  rose: "bg-rose-50 text-rose-600",
};

const SIZE_CLS: Record<NonNullable<EmptyStateProps["size"]>, string> = {
  compact: "py-10",
  default: "py-16",
  hero: "py-24",
};

/**
 * Empty state humano — patrón de la skill section 6.7. Reemplaza
 * "Sin resultados" y tablas vacías por una pista visual + descripción
 * contextual + CTA cuando aplica.
 *
 * Reglas:
 * - Title corto, declarativo. "Aún no hay socios", no "No data".
 * - Description aporta contexto + invita a actuar. Si no hay nada
 *   útil que decir, omitirla.
 * - children = 1-2 botones. Si no hay acción posible, dejar vacío.
 *
 * Anti-pattern: usar este componente para errores. Para errores va el
 * patrón rose-50 con AlertCircle directamente en el caller.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  children,
  tone = "brand",
  size = "default",
  className,
}: EmptyStateProps): JSX.Element {
  return (
    <div
      className={twMerge(
        "flex flex-col items-center justify-center px-6 text-center",
        SIZE_CLS[size],
        className,
      )}
    >
      {Icon && (
        <div
          className={twMerge(
            "grid place-items-center rounded-full p-3 mb-4",
            TONE_CLS[tone],
          )}
        >
          <Icon className="h-6 w-6" aria-hidden="true" />
        </div>
      )}
      <h3 className="text-sm font-semibold text-neutral-900">
        {title}
      </h3>
      {description && (
        <p className="mt-1 text-sm text-neutral-600 max-w-sm">
          {description}
        </p>
      )}
      {children && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {children}
        </div>
      )}
    </div>
  );
}
